import { z } from "zod";

// AI object detections as metadata, not burned into the video (#296, spik #294). The detector on
// the Mac runs YOLO and sends only the boxes; the panel draws them on a canvas over the untouched,
// low-latency WebRTC video (#288). The model computes once on the Mac, so weak client devices only
// draw rectangles instead of running a model (the browser COCO-SSD path, #257, overloaded them).
//
// It is an aid, not an oracle (ADR-0020): a missing or stale box never means "nobody is there", and
// a search area is never cleared on the strength of it. The panel draws nothing when it has no fresh
// match, so absence of boxes is always the safe default.

const Id = z.uuid();

/** People stand out most, then animals, then other objects. The panel colours boxes by this. */
export const DetectionCategory = z.enum(["person", "animal", "object"]);
export type DetectionCategory = z.infer<typeof DetectionCategory>;

/** One detected box. Coordinates are normalized 0..1 of the frame, so they are resolution- and
 *  aspect-independent: the panel multiplies by the rendered video size at draw time. */
export const Detection = z.object({
  /** [left, top, width, height], each 0..1 of the frame. */
  box: z.tuple([
    z.number().min(0).max(1),
    z.number().min(0).max(1),
    z.number().min(0).max(1),
    z.number().min(0).max(1),
  ]),
  /** Polish label from the detector's PL_LABELS (e.g. "osoba", "samochód"). */
  label: z.string(),
  category: DetectionCategory,
  score: z.number().min(0).max(1),
  /** Optional per-object track id, for a future hold/fade; absent in the first increment. */
  trackId: z.string().optional(),
});
export type Detection = z.infer<typeof Detection>;

/**
 * All detections for ONE frame of ONE camera, keyed to the frame's media time so the panel can
 * match them to the exact rendered video frame via requestVideoFrameCallback (spik #294 §2).
 */
export const DetectionFrame = z.object({
  /** The camera these belong to (video_sources.id). */
  sourceId: Id,
  /**
   * Frame presentation time in SECONDS of media time (OpenCV CAP_PROP_POS_MSEC / 1000). Matched in
   * the browser against requestVideoFrameCallback's metadata.mediaTime — both speak the stream's own
   * clock, so the Mac's and the browser's wall clocks never need to agree.
   */
  mediaTime: z.number().nonnegative(),
  /** Detector wall-clock ms, for diagnostics and staleness only — never used to match frames. */
  emittedAt: z.number().int().nonnegative(),
  detections: z.array(Detection),
});
export type DetectionFrame = z.infer<typeof DetectionFrame>;

/** Box colour by category: people stand out most, then animals, then objects. */
export function detectionColor(category: DetectionCategory): string {
  switch (category) {
    case "person":
      return "#ef4444"; // red-500
    case "animal":
      return "#f59e0b"; // amber-500
    case "object":
      return "#38bdf8"; // sky-400
  }
}
